import { useEffect, useRef, useState } from 'react';

const publicUrl = process.env.PUBLIC_URL;

function PixelButton({ kind, label, onPress }) {
  return (
    <button
      type="button"
      className={`desk_window_btn desk_window_btn_${kind}`}
      aria-label={label}
      title={label}
      onPointerDown={onPress}
    >
      <span className="desk_window_btn_tint" aria-hidden="true">
        <img
          className="desk_window_btn_icon desk_window_btn_icon_up"
          src={`${publicUrl}/icons/window/light_button_off.png`}
          alt=""
          width={16}
          height={16}
          draggable={false}
        />
        <img
          className="desk_window_btn_icon desk_window_btn_icon_down"
          src={`${publicUrl}/icons/window/light_button_on.png`}
          alt=""
          width={16}
          height={16}
          draggable={false}
        />
      </span>
    </button>
  );
}

export default function DesktopWindow({
  id,
  title,
  zIndex,
  initial,
  layout,
  stacked = false,
  raiseSignal = 0,
  onFocus,
  children,
  fitContent = false,
  minimizeLabel = 'Minimize',
  maximizeLabel = 'Maximize',
  restoreLabel = 'Restore',
}) {
  const rootRef = useRef(null);
  const [pos, setPos] = useState({ x: initial.x, y: initial.y });
  const [size, setSize] = useState({
    width: initial.width,
    height: initial.height ?? 280,
  });
  const [dragging, setDragging] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const savedRef = useRef({
    x: initial.x,
    y: initial.y,
    width: initial.width,
    height: initial.height ?? 280,
  });
  const posRef = useRef(pos);
  posRef.current = pos;
  const pinnedRef = useRef(false);

  const layoutX = layout?.x;
  const layoutY = layout?.y;
  const layoutWidth = layout?.width;
  const layoutHeight = layout?.height;

  useEffect(() => {
    if (
      stacked ||
      maximized ||
      dragging ||
      layoutX == null ||
      layoutY == null ||
      layoutWidth == null ||
      pinnedRef.current
    ) {
      return;
    }
    setPos({ x: layoutX, y: layoutY });
    setSize((prev) => ({
      width: layoutWidth,
      height: layoutHeight ?? prev.height,
    }));
    savedRef.current = {
      x: layoutX,
      y: layoutY,
      width: layoutWidth,
      height: layoutHeight ?? savedRef.current.height,
    };
  }, [stacked, maximized, dragging, layoutX, layoutY, layoutWidth, layoutHeight]);

  useEffect(() => {
    if (stacked) {
      pinnedRef.current = false;
      setMaximized(false);
      setMinimized(false);
    }
  }, [stacked]);

  useEffect(() => {
    if (raiseSignal > 0) {
      setMinimized(false);
    }
  }, [raiseSignal]);

  function currentHeight() {
    return rootRef.current?.offsetHeight ?? size.height;
  }

  function onTitlePointerDown(event) {
    if (stacked || event.button !== 0) {
      return;
    }
    if (event.target.closest('button')) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    onFocus(id);

    if (maximized) {
      return;
    }

    const pointerId = event.pointerId;
    const startX = event.clientX;
    const startY = event.clientY;
    const originX = posRef.current.x;
    const originY = posRef.current.y;

    setDragging(true);

    function onMove(moveEvent) {
      if (moveEvent.pointerId !== pointerId) {
        return;
      }
      moveEvent.preventDefault();
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      setPos({
        x: Math.max(0, originX + dx),
        y: Math.max(0, originY + dy),
      });
    }

    function onUp(upEvent) {
      if (upEvent.pointerId !== pointerId) {
        return;
      }
      pinnedRef.current = true;
      savedRef.current = {
        ...savedRef.current,
        x: posRef.current.x,
        y: posRef.current.y,
      };
      setDragging(false);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    }

    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }

  function restoreFromSaved() {
    setPos({ x: savedRef.current.x, y: savedRef.current.y });
    setSize({
      width: savedRef.current.width,
      height: savedRef.current.height,
    });
    setMinimized(false);
    setMaximized(false);
  }

  function onMinimize(event) {
    event.preventDefault();
    event.stopPropagation();
    onFocus(id);

    if (stacked) {
      return;
    }

    if (maximized) {
      restoreFromSaved();
      return;
    }

    if (!minimized) {
      savedRef.current = {
        x: pos.x,
        y: pos.y,
        width: size.width,
        height: currentHeight(),
      };
      setMinimized(true);
      setMaximized(false);
    }
  }

  function onMaximize(event) {
    event.preventDefault();
    event.stopPropagation();
    onFocus(id);

    if (stacked) {
      return;
    }

    if (minimized) {
      setMinimized(false);
      return;
    }

    if (maximized) {
      restoreFromSaved();
      return;
    }

    savedRef.current = {
      x: pos.x,
      y: pos.y,
      width: size.width,
      height: currentHeight(),
    };
    const pad = 16;
    const menu = 36;
    setPos({ x: pad, y: pad });
    setSize({
      width: Math.max(280, window.innerWidth - pad * 2),
      height: Math.max(220, window.innerHeight - menu - pad * 2 - 72),
    });
    setMaximized(true);
  }

  const showMinimize = !stacked && !minimized;
  const showMaximize = !stacked && !maximized;
  const useAutoHeight = fitContent && !maximized && !minimized;

  return (
    <section
      ref={rootRef}
      id={`window-${id}`}
      className={`desk_window${dragging ? ' is-dragging' : ''}${
        minimized ? ' is-minimized' : ''
      }${maximized ? ' is-maximized' : ''}${fitContent ? ' desk_window_fit' : ''}${
        stacked ? ' is-stacked' : ''
      }`}
      style={
        stacked
          ? { width: '100%', zIndex }
          : {
              left: pos.x,
              top: pos.y,
              width: size.width,
              height: minimized || useAutoHeight ? undefined : size.height,
              zIndex,
            }
      }
      onPointerDown={() => onFocus(id)}
    >
      <div className="desk_window_title" onPointerDown={onTitlePointerDown}>
        <div className="desk_window_controls">
          {showMinimize ? (
            <PixelButton
              kind="minimize"
              label={maximized ? restoreLabel : minimizeLabel}
              onPress={onMinimize}
            />
          ) : null}
          {showMaximize ? (
            <PixelButton
              kind="maximize"
              label={minimized ? restoreLabel : maximizeLabel}
              onPress={onMaximize}
            />
          ) : null}
        </div>
        <h2 className="desk_window_title_text">{title}</h2>
        <span aria-hidden="true" />
      </div>
      {minimized ? null : <div className="desk_window_body">{children}</div>}
    </section>
  );
}
